import { PureComponent } from "react";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { compose } from "redux";
import { injectIntl } from "react-intl";
import PropTypes from "prop-types";

import { Input, Grid, Col, CheckMark, Section, Dropdown, DependOn } from "../../components";
import { Txt, Button, Title } from "../../elements";
import { messages, constants } from "../../constants";
import { countries, provinces } from "../../constants/options";

import { validationBuilder, validations } from "../../containers/form";
import { validateEmail } from "../../util";
import { EmailFormData } from "../../util/emailFormData";


import MembershipCardRequestImage from "../../assets/img/membershipCardRequest.png";
import RequiredFieldFooterNote from "../../containers/form/fieldGroups/requiredFieldFooterNote";


const settings = {
  form: constants.FORM.membershipCardRequestForm,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnChange: true,
  initialValues: {
    country: "CAN"
  }
};

class MembershipCardRequest extends PureComponent {
  rulePhone = [];
  acceptedCountries = ["CAN"];
  state = {
    clientSideValidationError: null,
    resetSuccess: false,
    selectedCountry: ""
  };

  constructor(props) {
    super(props);

    const rules = Object.fromEntries(
      Object.entries(validations).map(([key, validation]) => [
        key,
        (spec, overrideErrorMessage = false) => (value) => {
          return validationBuilder(props, validation, spec, overrideErrorMessage)(value)
        }
      ])
    );
    const { intl } = props;
    this.ruleFirstName = [
      rules.required(intl.formatMessage(messages.personal.firstName)),
      rules.names(intl.formatMessage(messages.error.firstNameFormat), true)
    ];
    this.ruleLastName = [
      rules.required(intl.formatMessage(messages.personal.lastName)),
      rules.names(intl.formatMessage(messages.error.lastNameFormat), true)
    ];
    this.ruleEmail = [
      rules.required(intl.formatMessage(messages.field.evoLoginEmail)),
      rules.email()
    ];
    this.rulePostalCode = [
      rules.required(intl.formatMessage(messages.address.postalCode)),
      rules.postalCode()
    ];
    this.ruleMailingAddress = [
      rules.required(intl.formatMessage(messages.address.addressLine, { type: "Mailing address"}))
    ];
    this.ruleCity = [
      rules.required(intl.formatMessage(messages.address.city))
    ];
    this.ruleCountry = [
      rules.required(intl.formatMessage(messages.address.country))
    ];
    this.ruleProvince = [
      rules.required(intl.formatMessage(messages.address.province))
    ];
    this.ruleState = [
      rules.required(intl.formatMessage(messages.address.state))
    ];
  }

  sendEmail = async (payload) => {
    const { intl } = this.props;
    if (!validateEmail(payload.email)) {
      this.setState({ clientSideValidationError: intl.formatMessage(messages.error.email) });
      return null;
    }
    const emailFormData = new EmailFormData(payload, constants.EMAIL_TYPE.MEMBERSHIP_CARD)

    try {
      const res = await fetch(constants.ENDPOINTS.sendEmail, {
        method: "POST",
        body: emailFormData.getFormData()
      });

      if (!res.ok) {
        this.setState({
          clientSideValidationError: "Something went wrong!",
          resetSuccess: false
        });
      } else {
        this.setState({
          clientSideValidationError: null,
          resetSuccess: true
        });
      }
    } catch (error) {
      this.setState({
        clientSideValidationError: error.message,
      });
    }
  };

  renderSuccessfulScreen = () => {
    const { intl } = this.props;

    return (
      <div className="reset-code" data-testid="membership-card-request-success" id="membership-card-request-success">
        <Grid theme={["center"]}>
          <Col size="1-2" min="sm">
            <CheckMark />
            <Title
              priority={1}
              type={["strong"]}
              style={{
                padding: "50px"
              }}
              className="h1 reset-code__title blue"
            >
              {intl.formatMessage(messages.membershipCardRequest.successMessage)}
            </Title>
            <Title priority={5} className="h5 reset-code__title">
              {intl.formatMessage(messages.membershipCardRequest.successMessageDescription)}
            </Title>
          </Col>
        </Grid>
      </div>
    );
  };

  render() {
    const { submitting, invalid, pristine, intl, handleSubmit, selectedCountry } = this.props;

    if (this.state.clientSideValidationError) {
      return (
        <div data-testid="membership-card-request-error" id="membership-card-request-error" style={{ display: "contents" }}>
          {this.state.clientSideValidationError}
        </div>
      );
    }

    if (this.state.resetSuccess) {
      return this.renderSuccessfulScreen();
    }
    return (
      <div className="outerForm">
        <img
          style={{ width: "100%", height: "400px", objectFit: "cover" }}
          src={MembershipCardRequestImage}
          alt="two men sat in an evo car"
        />
        <div style={{ padding: "30px 0" }}>
          <form
            model={constants.FORM.membershipCardRequestForm}
            onSubmit={handleSubmit(this.sendEmail)}
            noValidate
          >
            <Title priority={2} type={["strong-no-cap"]}>
              {intl.formatMessage(messages.membershipCardRequest.title)}
            </Title>
            <Txt theme={[`middle`, `large`]}>
              {intl.formatMessage(messages.membershipCardRequest.description)}
            </Txt>
            <Section size="2-3" min="md">
              <Grid>
                <Col size="3-4" min="sm">
                  <Field
                    type="text"
                    name="firstName"
                    label={intl.formatMessage(messages.personal.firstName)}
                    required={true}
                    component={Input}
                    validate={this.ruleFirstName}
                    props={{ maxLength: "50" }}
                  />
                </Col>

                <Col size="3-4" min="sm">
                  <Field
                    type="text"
                    name="lastName"
                    label={intl.formatMessage(messages.personal.lastName)}
                    required={true}
                    component={Input}
                    validate={this.ruleLastName}
                    props={{ maxLength: "50" }}
                  />
                </Col>

                <Col size="3-4" min="sm">
                  <Field
                    type="email"
                    name="email"
                    label={intl.formatMessage(messages.field.evoLoginEmail)}
                    required={true}
                    validate={this.ruleEmail}
                    component={Input}
                    props={{ maxLength: "50" }}
                  />
                </Col>

                <Col size="3-4" min="sm">
                  <br /><br />
                  <Title priority={2} type={["strong-no-cap"]}>
                    {intl.formatMessage(messages.title.MailingAddress)}
                  </Title>
                </Col>

                <Col size="1-3" min="sm">
                  <Field
                    type="text"
                    name="unitNumber"
                    label={intl.formatMessage(messages.address.unitNumber)}
                    component={Input}
                    props={{ maxLength: "45" }}
                  />
                </Col>
                
                <Col size="2-3" min="sm">
                  <Field
                    type="text"
                    name="mailingAddress"
                    label={intl.formatMessage(messages.address.addressLine, { type: "Mailing address"})}
                    component={Input}
                    props={{ maxLength: "50" }}
                    required={true}
                    validate={this.ruleMailingAddress}
                  />
                </Col>

                <Col size="1-2" min="sm">
                  <Field
                    type="text"
                    name="city"
                    label={intl.formatMessage(messages.address.city)}
                    component={Input}
                    props={{ maxLength: "50" }}
                    required={true}
                    validate={this.ruleCity}
                  />
                </Col>
              </Grid>

              <Grid>
                <Col size="1-2" min="sm">
                  <Field
                    name="country"
                    label={intl.formatMessage(messages.address.country)}
                    required={true}
                    validate={this.ruleCountry}
                    options={countries(intl).filter(country => this.acceptedCountries.includes(country.value))}
                    component={Dropdown}
                  />
                </Col>

                <Col size="1-2" min="sm">
                  <DependOn active={selectedCountry === "CAN"}>
                    <Field
                      name="province"
                      label={intl.formatMessage(messages.address.province)}
                      required={true}
                      validate={this.ruleProvince}
                      options={provinces(intl)}
                      component={Dropdown}
                    />
                  </DependOn>
                </Col>
              </Grid>

              <Grid>

                <Col size="3-4" min="sm">
                  <DependOn active={selectedCountry === "CAN"}>
                    <Field
                      type="text"
                      name="postalCode"
                      label={intl.formatMessage(messages.address.postalCode)}
                      required={true}
                      validate={this.rulePostalCode}
                      component={Input}
                      props={{ maxLength: "8" }}
                    />
                  </DependOn>
                </Col>
              </Grid>
              <Col size="3-4" min="sm">
                <RequiredFieldFooterNote />
              </Col>
            </Section>
            <br />
            <Button
              loading={submitting}
              type="submit"
              theme={["cta"]}
              disabled={invalid || submitting || pristine}
            >
              {intl.formatMessage(messages.button.submit)}
            </Button>
          </form>
        </div>
      </div>
    );
  }
}

MembershipCardRequest.propTypes = {
  intl: PropTypes.object.isRequired,
  submitting: PropTypes.bool
};

MembershipCardRequest.defaultProps = {
  button: "",
  submitting: false
};

const mapStateToProps = (state) => {
  const selector = formValueSelector(constants.FORM.membershipCardRequestForm);
  return {
    state,
    selectedCountry: selector(state, 'country')
  };
};

const enhancer = compose(injectIntl, connect(mapStateToProps), reduxForm(settings));

export default enhancer(MembershipCardRequest);
